import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface OnboardingDescribedOptionCardProps {
  label: string;
  description?: string;
  selected: boolean;
  /** Shared across the group, so arrow keys move between the cards. */
  name: string;
  onSelect: () => void;
  /**
   * Shown inside the card under the row while it is selected — the "How did
   * you hear about us?" free-text field. Outside the `<label>`, so typing in
   * it does not re-select the radio or fold a text box into its name.
   */
  children?: ReactNode;
}

/**
 * Single-choice card with a title, a supporting line and a trailing radio
 * (`16133:189205`, the "How did you hear about us?" rows).
 *
 * `OnboardingOptionRow`'s radio and selected treatment, with a second line of
 * text and room underneath for a follow-up field. A real
 * `<input type="radio">` under the drawn ring, as in the other steps.
 */
const OnboardingDescribedOptionCard = ({
  label,
  description,
  selected,
  name,
  onSelect,
  children,
}: OnboardingDescribedOptionCardProps) => (
  <div
    className={cn(
      "flex flex-col rounded-lg transition-colors",
      "has-[input[type=radio]:focus-visible]:outline has-[input[type=radio]:focus-visible]:outline-2 has-[input[type=radio]:focus-visible]:outline-offset-2 has-[input[type=radio]:focus-visible]:outline-focusRing-default",
      selected
        ? "border-[1.5px] border-border-token-brand-subtle bg-bg-brand-tertiary-default"
        : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover",
    )}
  >
    {/* Frame: 16 across, 12 down; 8 between the row and the field under a
        selected card, which then closes on 16. */}
    <label
      className={cn(
        "flex min-h-[48px] cursor-pointer items-center gap-2 px-4 py-3",
        selected && children ? "pb-2" : null,
      )}
    >
      <input
        type="radio"
        name={name}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
      />

      <span className="flex min-w-0 flex-1 flex-col px-1">
        <span className="body-base-medium text-fg-neutral-strong">{label}</span>
        {description ? (
          <span className="body-s-regular text-fg-neutral-subtle">
            {description}
          </span>
        ) : null}
      </span>

      {/* Decorative: the input's own checked state already conveys this. */}
      <span
        aria-hidden="true"
        className="flex min-w-[32px] shrink-0 items-center justify-end"
      >
        <span
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] bg-bg-brand-secondary-default",
            selected
              ? "border-border-token-brand-subtle"
              : "border-border-token-neutral-strong",
          )}
        >
          {selected ? (
            <span className="h-3 w-3 rounded-full bg-fg-brand-subtle" />
          ) : null}
        </span>
      </span>
    </label>

    {selected && children ? <div className="px-4 pb-4">{children}</div> : null}
  </div>
);

export default OnboardingDescribedOptionCard;
